// js/export.js — CSV and JSON download for a set of entries

import { getProjects, getCategories, getEntries } from './data.js';

/** Resolve IDs to names for a single entry (projects/categories pre-fetched) */
function resolveEntry(entry, projects, categories) {
  const proj = projects.find(p => p.id === entry.projectId);
  const cat  = categories.find(c => c.id === entry.categoryId);
  return {
    date:        entry.date,
    day:         new Date(entry.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long' }),
    user:        entry.userName || '',
    project:     proj?.name  || '',
    category:    cat?.name   || '',
    start:       entry.startTime || '',
    end:         entry.endTime   || '',
    duration:    entry.duration  || 0,
    description: entry.description || '',
  };
}

/** Trigger a file download in the browser */
function download(filename, content, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Build CSV string from entries + lookup tables */
async function buildCSV(entries) {
  const [projects, categories] = await Promise.all([getProjects(), getCategories()]);
  const COLS = ['date','day','user','project','category','start','end','duration','description'];
  const header = COLS.join(',');
  const rows = entries.map(e => {
    const r = resolveEntry(e, projects, categories);
    return COLS.map(col => {
      const val = String(r[col] ?? '');
      return val.includes(',') || val.includes('"') || val.includes('\n')
        ? `"${val.replace(/"/g, '""')}"` : val;
    }).join(',');
  });
  return [header, ...rows].join('\n');
}

/** Download entries as CSV */
export async function exportCSV(entries, filename = 'timelogger-export.csv') {
  const csv = await buildCSV(entries);
  download(filename, csv, 'text/csv');
}

/** Download ALL entries (all users, all time) as CSV */
export async function exportAllCSV() {
  const entries = await getEntries();
  const today   = new Date().toISOString().slice(0, 10);
  const csv     = await buildCSV(entries);
  download(`vimond-timelogger-full-export-${today}.csv`, csv, 'text/csv');
}

/** Download entries as JSON (resolved names, not IDs) */
export async function exportJSON(entries, filename = 'timelogger-export.json') {
  const [projects, categories] = await Promise.all([getProjects(), getCategories()]);
  const resolved = entries.map(e => resolveEntry(e, projects, categories));
  download(filename, JSON.stringify(resolved, null, 2), 'application/json');
}
